import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isAdmin } from '@mdt/domain';
import { Button, Loading } from '../components/kit.tsx';
import { messageText } from '../lib/i18n.ts';
import { SafeLink } from '../components/SafeLink.tsx';
import { usePageTitle } from '../lib/format.ts';
import { useRouter } from '../lib/router.tsx';
import { useSession } from '../lib/session.tsx';
import { AdminCohort } from '../screens/AdminCohort.tsx';
import { AdminCohorts } from '../screens/AdminCohorts.tsx';
import { AdminNotifications } from '../screens/AdminNotifications.tsx';
import { EmergencyPhone } from '../screens/EmergencyPhone.tsx';
import { Group } from '../screens/Group.tsx';
import { Home } from '../screens/Home.tsx';
import { Install } from '../screens/Install.tsx';
import { Login } from '../screens/Login.tsx';
import { Onboarding } from '../screens/Onboarding.tsx';
import { Settings } from '../screens/Settings.tsx';
import { SignedOut } from '../screens/SignedOut.tsx';
import { Shell } from './Shell.tsx';

const FormKit = __MDT_TEST_PAGES__ ? lazy(() => import('../screens/FormKit.tsx').then((m) => ({ default: m.FormKit }))) : null;
// Phase 5 screens (and the upload engine) load on first use: most visits never need them.
const Deliverables = lazy(() => import('../screens/Deliverables.tsx').then((m) => ({ default: m.Deliverables })));
const AdminRounds = lazy(() => import('../screens/AdminRounds.tsx').then((m) => ({ default: m.AdminRounds })));
const AdminRound = lazy(() => import('../screens/AdminRound.tsx').then((m) => ({ default: m.AdminRound })));
// Phase 6 screens: used by one cohort a few weeks a year, so they load on first use too.
const Matching = lazy(() => import('../screens/Matching.tsx').then((m) => ({ default: m.Matching })));
const Inbox = lazy(() => import('../screens/Inbox.tsx').then((m) => ({ default: m.Inbox })));
const Supervision = lazy(() => import('../screens/Supervision.tsx').then((m) => ({ default: m.Supervision })));
const AdminMatching = lazy(() => import('../screens/AdminMatching.tsx').then((m) => ({ default: m.AdminMatching })));
// Phase 7: group changes after matching, an admin's occasional task.
const AdminProject = lazy(() => import('../screens/AdminProject.tsx').then((m) => ({ default: m.AdminProject })));
// Phase 8: the scheduling console is an admin's tool for a few weeks a year; everyone's schedule view is light.
const AdminSchedule = lazy(() => import('../screens/AdminSchedule.tsx').then((m) => ({ default: m.AdminSchedule })));
const Schedule = lazy(() => import('../screens/Schedule.tsx').then((m) => ({ default: m.Schedule })));
// Phase 9: scoring (evaluators, a few days a round), results (students), rubrics and grading (admins).
const Scoring = lazy(() => import('../screens/Scoring.tsx').then((m) => ({ default: m.Scoring })));
const ScoringSheet = lazy(() => import('../screens/ScoringSheet.tsx').then((m) => ({ default: m.ScoringSheet })));
const Results = lazy(() => import('../screens/Results.tsx').then((m) => ({ default: m.Results })));
const AdminRubrics = lazy(() => import('../screens/AdminRubrics.tsx').then((m) => ({ default: m.AdminRubrics })));
const AdminGrading = lazy(() => import('../screens/AdminGrading.tsx').then((m) => ({ default: m.AdminGrading })));
const AdminGrades = lazy(() => import('../screens/AdminGrades.tsx').then((m) => ({ default: m.AdminGrades })));
// Phase 10: clearance, once per student at the end of the course (signers work their queues for a few weeks).
const Clearance = lazy(() => import('../screens/Clearance.tsx').then((m) => ({ default: m.Clearance })));
const ClearanceStudent = lazy(() => import('../screens/Clearance.tsx').then((m) => ({ default: m.ClearanceStudent })));
const CertificateVerify = lazy(() => import('../screens/CertificateVerify.tsx').then((m) => ({ default: m.CertificateVerify })));
// Phase 11: the admin console, ghost-mode reports, the audit explorer, operations, exports, and the person's own record.
const AdminConsole = lazy(() => import('../screens/AdminConsole.tsx').then((m) => ({ default: m.AdminConsole })));
const AdminPeople = lazy(() => import('../screens/AdminPeople.tsx').then((m) => ({ default: m.AdminPeople })));
const AdminGhost = lazy(() => import('../screens/AdminGhost.tsx').then((m) => ({ default: m.AdminGhost })));
const AdminAudit = lazy(() => import('../screens/AdminAudit.tsx').then((m) => ({ default: m.AdminAudit })));
const AdminOps = lazy(() => import('../screens/AdminOps.tsx').then((m) => ({ default: m.AdminOps })));
const Exports = lazy(() => import('../screens/Exports.tsx').then((m) => ({ default: m.Exports })));
const Activity = lazy(() => import('../screens/Activity.tsx').then((m) => ({ default: m.Activity })));
// Phase 12: the legacy cutover, a few days a year until the legacy systems are retired.
const AdminCutover = lazy(() => import('../screens/AdminCutover.tsx').then((m) => ({ default: m.AdminCutover })));
const later = (el: React.JSX.Element) => <Suspense fallback={<Loading />}>{el}</Suspense>;

function NotFound() {
  const { t } = useTranslation();
  usePageTitle(t('notFound.title'));
  return (
    <div className="stack">
      <h1>{t('notFound.title')}</h1>
      <p><SafeLink href="/">{t('shell.home')}</SafeLink></p>
    </div>
  );
}

/** Screens that need no session: sign-in, onboarding (ticket cookie), signed-out, install guide. */
const PUBLIC: Record<string, () => React.JSX.Element> = {
  '/onboarding': Onboarding,
  '/signed-out': SignedOut,
  '/install': Install,
};

function Routes() {
  const { path } = useRouter();
  const { state } = useSession();
  const Public = PUBLIC[path];
  if (Public) return <Public />;
  if (state.status === 'loading') return <Loading />;
  if (state.status === 'signedOut') return <Login reasonKey={state.messageKey} />;

  const view = state.view;
  if (view.required.includes('emergency_phone')) return <EmergencyPhone />;
  const admin = isAdmin({ grants: view.grants.map((g) => ({ grantId: '', ...g })) });
  const roles = new Set(view.grants.map((g) => g.role));
  // departmental staff (spec §7): who downloads and verifies certificates
  const deptStaff = admin || roles.has('lab_officer') || roles.has('research_officer');
  switch (path) {
    case '/':
    case '/login':
      return <Home />;
    case '/settings':
      return <Settings />;
    case '/group':
      return view.person.kind === 'student' ? <Group /> : <NotFound />;
    case '/deliverables':
      return view.person.kind === 'student' ? later(<Deliverables />) : <NotFound />;
    case '/matching':
      return view.person.kind === 'student' ? later(<Matching />) : <NotFound />;
    case '/inbox':
      return view.person.kind === 'staff' ? later(<Inbox />) : <NotFound />;
    case '/supervision':
      return view.person.kind === 'staff' ? later(<Supervision />) : <NotFound />;
    case '/admin/matching':
      return admin ? later(<AdminMatching />) : <NotFound />;
    case '/admin/cohorts':
      return admin ? <AdminCohorts /> : <NotFound />;
    case '/admin/cohort':
      return admin ? <AdminCohort /> : <NotFound />;
    case '/admin/project':
      return admin ? later(<AdminProject />) : <NotFound />;
    case '/admin/rounds':
      return admin ? later(<AdminRounds />) : <NotFound />;
    case '/admin/round':
      return admin ? later(<AdminRound />) : <NotFound />;
    case '/admin/schedule':
      return admin ? later(<AdminSchedule />) : <NotFound />;
    case '/schedule':
      return later(<Schedule />);
    case '/scoring':
      return view.person.kind === 'staff' ? later(<Scoring />) : <NotFound />;
    case '/scoring/sheet':
      return view.person.kind === 'staff' ? later(<ScoringSheet />) : <NotFound />;
    case '/results':
      return view.person.kind === 'student' ? later(<Results />) : <NotFound />;
    case '/admin/rubrics':
      return admin ? later(<AdminRubrics />) : <NotFound />;
    case '/admin/grading':
      return admin ? later(<AdminGrading />) : <NotFound />;
    case '/admin/grades':
      return admin ? later(<AdminGrades />) : <NotFound />;
    case '/clearance':
      return view.person.kind === 'student' || roles.size > 0 ? later(<Clearance />) : <NotFound />;
    case '/clearance/student':
      return view.person.kind === 'staff' && roles.size > 0 ? later(<ClearanceStudent />) : <NotFound />;
    case '/clearance/verify':
      return deptStaff ? later(<CertificateVerify />) : <NotFound />;
    case '/admin/notifications':
      return admin ? <AdminNotifications /> : <NotFound />;
    case '/admin':
      return admin ? later(<AdminConsole />) : <NotFound />;
    case '/admin/people':
      return admin ? later(<AdminPeople />) : <NotFound />;
    case '/admin/ghost':
      return admin ? later(<AdminGhost />) : <NotFound />;
    case '/admin/audit':
      return admin ? later(<AdminAudit />) : <NotFound />;
    case '/admin/ops':
      return admin ? later(<AdminOps />) : <NotFound />;
    case '/admin/cutover':
      return admin ? later(<AdminCutover />) : <NotFound />;
    case '/exports':
      return roles.size > 0 && !view.ghost ? later(<Exports />) : <NotFound />;
    case '/activity':
      return view.ghost ? <NotFound /> : later(<Activity />);
    case '/dev/form-kit':
      return FormKit ? <Suspense fallback={<Loading />}><FormKit /></Suspense> : <NotFound />;
    default:
      return <NotFound />;
  }
}

/**
 * A screen that throws while rendering shows a way out instead of a blank page. Drafts were saved as the
 * person typed, so reloading loses at most the last second.
 */
class ScreenBoundary extends Component<{ children: ReactNode; path: string }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error('screen failed', error, info.componentStack);
  }

  override componentDidUpdate(prev: { path: string }) {
    if (prev.path !== this.props.path && this.state.failed) this.setState({ failed: false });
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="alert alert--error" role="alert">
        <p>{messageText('err.internal')}</p>
        <Button onClick={() => globalThis.location.reload()}>{messageText('update.reload')}</Button>
      </div>
    );
  }
}

export function App() {
  const { path } = useRouter();
  return (
    <Shell>
      <ScreenBoundary path={path}>
        <Routes />
      </ScreenBoundary>
    </Shell>
  );
}
